// Updated product-form custom element
// - Adds variant/change handling to update discount badge when variant changes
// - Observes price element for dynamic updates and keeps badge in sync
// - Helper to parse money strings to numbers
// - Safe: only manipulates DOM inside this form/product scope

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';

      // initialize variant listeners and badge sync
      this.initVariantHandlers();
      // initial sync of badge (if price markup already present)
      this.updateDiscountBadge();
    }

    /* -------------------------
       Submit flow (unchanged)
       ------------------------- */
    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      //this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          //this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    /* -------------------------
       VARIANT / BADGE LOGIC
       ------------------------- */

    initVariantHandlers() {
      // Listen for change on variant input (name="id") - common pattern in Shopify themes
      try {
        const variantInput = this.form.querySelector('[name="id"]');
        if (variantInput) {
          variantInput.addEventListener('change', () => {
            // Small delay to let other handlers update the price markup first
            setTimeout(() => this.updateDiscountBadge(), 30);
          });
        }

        // Some themes emit a custom event on variant change — listen for common names
        const events = ['variant:change', 'variant:modified', 'variant:change:after', 'variantChange'];
        events.forEach(ev => {
          document.addEventListener(ev, (e) => {
            // If the event carries element detail, ensure it's related to this form/product
            if (e && e.detail && e.detail.container) {
              const container = (typeof e.detail.container === 'string') ? document.querySelector(e.detail.container) : e.detail.container;
              if (container && (this.contains(container) || container.contains(this))) {
                setTimeout(() => this.updateDiscountBadge(), 30);
              }
            } else {
              // Fallback: run update if price element is inside this form/product
              setTimeout(() => this.updateDiscountBadge(), 30);
            }
          });
        });

        // Observe price element(s) inside this form for attribute or child changes (safe scoped observer)
        const priceEl = this.getPriceElement();
        if (priceEl) this.observePriceElement(priceEl);
      } catch (err) {
        // don't break if something unexpected happens
        console.warn('initVariantHandlers error', err);
      }
    }

    // Finds the price wrapper element inside this product/form scope
    getPriceElement() {
      // common selectors: .price (wrapper), .price__sale, .price__regular
      return this.querySelector('.price') || this.querySelector('.price__sale') || this.querySelector('.price__regular') || this.closest('.product')?.querySelector('.price');
    }

    // Observe the price element for dynamic changes (attributes and subtree)
    observePriceElement(priceEl) {
      if (!priceEl || priceEl.__priceObserverAttached) return;
      priceEl.__priceObserverAttached = true;

      const mo = new MutationObserver((mutations) => {
        // If data-price-value or data-compare-value change or childList changed, refresh badge
        for (const m of mutations) {
          if (m.type === 'attributes' && (m.attributeName === 'data-price-value' || m.attributeName === 'data-compare-value')) {
            this.updateDiscountBadge();
            return;
          }
          if (m.type === 'childList' || m.type === 'characterData' || m.type === 'subtree') {
            this.updateDiscountBadge();
            return;
          }
        }
      });

      mo.observe(priceEl, { attributes: true, attributeFilter: ['data-price-value','data-compare-value'], childList: true, subtree: true, characterData: true });
    }

    // Update (create, update, remove) discount badge inside the product/card scope
    updateDiscountBadge() {
      try {
        // Determine the container where we should place the badge:
        // prefer .product__info-container inside this form/product, otherwise the closest .card or product wrapper
        const productInfo = this.querySelector('.product__info-container') || this.closest('.product')?.querySelector('.product__info-container') || this.closest('.card') || this.closest('.product') || this;

        const priceWrapper = (this.getPriceElement() || productInfo?.querySelector('.price') || productInfo) ;

        if (!priceWrapper) return;

        // read numeric values from data attributes first
        let priceVal = parseFloat(priceWrapper.getAttribute('data-price-value'));
        let compareVal = parseFloat(priceWrapper.getAttribute('data-compare-value'));

        // fallback: parse visible price text nodes
        if (isNaN(priceVal)) {
          const saleEl = priceWrapper.querySelector('.price-item--sale, .price-item--price, .price-item');
          if (saleEl) priceVal = this.parseMoneyToNumber(saleEl.textContent);
        }
        if (isNaN(compareVal)) {
          const cmpEl = priceWrapper.querySelector('.price-item--regular');
          if (cmpEl) compareVal = this.parseMoneyToNumber(cmpEl.textContent);
        }

        // If values are still NaN, abort
        if (isNaN(priceVal) || isNaN(compareVal)) {
          // remove any existing badge (no valid compare)
          const existing = productInfo && productInfo.querySelector('.badge--discount');
          if (existing) existing.remove();
          priceWrapper.removeAttribute && priceWrapper.removeAttribute('data-discount-percent');
          return;
        }

        if (compareVal > priceVal) {
          const percent = Math.round(((compareVal - priceVal) / compareVal) * 100);

          // Update wrapper attribute
          priceWrapper.setAttribute('data-discount-percent', String(percent));

          // find existing badge within productInfo or priceWrapper
          let badge = (productInfo && productInfo.querySelector('.badge--discount')) || priceWrapper.querySelector('.badge--discount');

          // construct badge markup
          const ariaLabel = (window.theme && window.theme.quickviewMore) ? `${window.theme.quickviewMore} -${percent}%` : `On sale -${percent}%`;
          const badgeHtml = `<span class="badge badge--discount" role="status" aria-label="${ariaLabel}" data-discount-percent="${percent}"><span class="badge__dot" aria-hidden="true"></span><span class="badge__text">-${percent}%</span><span class="visually-hidden">${ariaLabel}</span></span>`;

          if (!badge) {
            // Try to insert into productInfo at the top, or before priceWrapper
            if (productInfo && productInfo.firstElementChild) {
              productInfo.insertAdjacentHTML('afterbegin', badgeHtml);
            } else if (priceWrapper && priceWrapper.parentElement) {
              priceWrapper.parentElement.insertAdjacentHTML('beforebegin', badgeHtml);
            } else {
              // last resort: append to this element
              this.insertAdjacentHTML('afterbegin', badgeHtml);
            }
          } else {
            // update existing badge text and attribute
            const textEl = badge.querySelector('.badge__text');
            if (textEl) textEl.textContent = `-${percent}%`;
            badge.setAttribute('data-discount-percent', String(percent));
            badge.setAttribute('aria-label', ariaLabel);
            const vis = badge.querySelector('.visually-hidden');
            if (vis) vis.textContent = ariaLabel;
          }
        } else {
          // no discount -> remove existing badge if present
          const existing = productInfo && productInfo.querySelector('.badge--discount') || priceWrapper.querySelector('.badge--discount');
          if (existing) existing.remove();
          priceWrapper.removeAttribute && priceWrapper.removeAttribute('data-discount-percent');
        }
      } catch (err) {
        console.warn('updateDiscountBadge error', err);
      }
    }

    // Helper: parse money string like "98.00 лв" or "$98.00" to 98.00 (number)
    parseMoneyToNumber(text) {
      if (!text) return NaN;
      // strip whitespace and non numeric except comma/dot/minus
      const normalized = String(text).replace(/\u00A0/g,' ').replace(/\s+/g, '').replace(/[^\d.,\-–]/g, '');
      const commaCount = (normalized.match(/,/g) || []).length;
      const dotCount = (normalized.match(/\./g) || []).length;
      let cleaned = normalized;
      if (commaCount && !dotCount) {
        // comma as decimal separator
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      } else if (dotCount && commaCount) {
        // remove commas (thousands), keep dot as decimal
        cleaned = cleaned.replace(/,/g, '');
      }
      const n = parseFloat(cleaned);
      return isNaN(n) ? NaN : n;
    }

  });
}